import {
  setDoc as fsSetDoc,
  updateDoc as fsUpdateDoc,
  deleteDoc as fsDeleteDoc,
  getDoc as fsGetDoc,
  getDocs as fsGetDocs,
  onSnapshot as fsOnSnapshot,
  writeBatch as fsWriteBatch
} from 'firebase/firestore'
import { trackRead, trackWrite, trackDelete } from '../utils/firestoreTracker'

// Auto-tracking Firestore interceptors to maintain exact daily Spark quota meters
export const setDoc: typeof fsSetDoc = (async (docRef: any, data: any, options?: any) => {
  const colName = docRef?.parent?.id || 'general'
  trackWrite(colName, 1)
  return options ? fsSetDoc(docRef, data, options) : fsSetDoc(docRef, data)
}) as typeof fsSetDoc

export const updateDoc: typeof fsUpdateDoc = (async (docRef: any, ...args: any[]) => {
  const colName = docRef?.parent?.id || 'general'
  trackWrite(colName, 1)
  return (fsUpdateDoc as any)(docRef, ...args)
}) as typeof fsUpdateDoc

export const deleteDoc: typeof fsDeleteDoc = (async (docRef: any) => {
  const colName = docRef?.parent?.id || 'general'
  trackDelete(colName, 1)
  return fsDeleteDoc(docRef)
}) as typeof fsDeleteDoc

export const getDoc: typeof fsGetDoc = (async (docRef: any) => {
  const colName = docRef?.parent?.id || 'general'
  const snap = await fsGetDoc(docRef)
  if (!snap?.metadata?.fromCache) {
    trackRead(colName, 1)
  }
  return snap
}) as typeof fsGetDoc

export const getDocs: typeof fsGetDocs = (async (queryRef: any) => {
  const colName = queryRef?._query?.path?.segments?.[0] || 'general'
  const snap = await fsGetDocs(queryRef)
  if (!snap?.metadata?.fromCache) {
    trackRead(colName, Math.max(1, snap?.size || 0))
  }
  return snap
}) as typeof fsGetDocs

export const onSnapshot: typeof fsOnSnapshot = ((queryOrDocRef: any, onNext: any, onError?: any) => {
  const colName = queryOrDocRef?._query?.path?.segments?.[0] || queryOrDocRef?.parent?.id || queryOrDocRef?.id || 'general'
  return fsOnSnapshot(queryOrDocRef, (snap: any) => {
    // Only track server-billed reads (cached reads cost 0 in Firebase Spark tier)
    if (!snap?.metadata?.fromCache) {
      const count = typeof snap?.docChanges === 'function' ? (snap.docChanges().length || snap.size || 1) : 1
      trackRead(colName, Math.max(1, count))
    }
    if (typeof onNext === 'function') {
      onNext(snap)
    } else if (onNext && typeof onNext.next === 'function') {
      onNext.next(snap)
    }
  }, onError)
}) as typeof fsOnSnapshot

export const writeBatch: typeof fsWriteBatch = (firestore: any) => {
  const batch = fsWriteBatch(firestore)
  if (!batch) return batch
  let pendingWrites = 0

  const origSet = typeof batch.set === 'function' ? batch.set.bind(batch) : null
  if (origSet) {
    batch.set = (ref: any, data: any, options?: any) => {
      pendingWrites++
      return origSet(ref, data, options)
    }
  }

  const origUpdate = typeof batch.update === 'function' ? batch.update.bind(batch) : null
  if (origUpdate) {
    batch.update = (ref: any, data: any) => {
      pendingWrites++
      return origUpdate(ref, data)
    }
  }

  const origDelete = typeof batch.delete === 'function' ? batch.delete.bind(batch) : null
  if (origDelete) {
    batch.delete = (ref: any) => {
      pendingWrites++
      return origDelete(ref)
    }
  }

  const origCommit = typeof batch.commit === 'function' ? batch.commit.bind(batch) : null
  if (origCommit) {
    batch.commit = async () => {
      if (pendingWrites > 0) {
        trackWrite('general', pendingWrites)
      }
      return origCommit()
    }
  }

  return batch
}
